import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import { useBandStore } from './bandStore'
import type { InviteDto, UserSearchResultDto, SendInviteDto } from '../dtos'

export const useInviteStore = defineStore('invite', () => {
    const userStore = useUserStore()
    const bandStore = useBandStore()
    
    const myInvites = ref<InviteDto[]>([])
    const bandInvites = ref<InviteDto[]>([])
    const searchResults = ref<UserSearchResultDto[]>([])
    const loading = ref(false)
    const initialLoading = ref(false)

    async function fetchMyInvites() {
        if (!userStore.user) return
        
        if (myInvites.value.length === 0) {
            initialLoading.value = true
        }

        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get('/invites/my', {
                headers: { Authorization: `Bearer ${token}` }
            })
            myInvites.value = response.data
        } catch (error) {
            console.error('Failed to fetch invites:', error)
        } finally {
            initialLoading.value = false
        }
    }

    async function fetchBandInvites(bandId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/invites/band/${bandId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            bandInvites.value = response.data
        } catch (error) {
            console.error('Failed to fetch band invites:', error)
        }
    }

    async function searchUsers(query: string) {
        if (!userStore.user || !bandStore.currentBand || query.length < 1) {
            searchResults.value = []
            return
        }
        loading.value = true
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/users/search?query=${query}&bandId=${bandStore.currentBand.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            searchResults.value = response.data
        } catch (error) {
            console.error('Failed to search users:', error)
        } finally {
            loading.value = false
        }
    }

    async function sendInvite(targetUserId: number) {
        if (!userStore.user || !bandStore.currentBand) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: SendInviteDto = {
                bandId: bandStore.currentBand.id,
                targetUserId: targetUserId
            }
            const response = await api.post('/invites', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            // Add to band invites immediately
            bandInvites.value.unshift(response.data)
        } catch (error) {
            console.error('Failed to send invite:', error)
            throw error
        }
    }

    async function acceptInvite(inviteId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.post(`/invites/${inviteId}/accept`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            await fetchMyInvites()
            await bandStore.fetchBands() // Refresh bands list
        } catch (error) {
            console.error('Failed to accept invite:', error)
        }
    }

    async function declineInvite(inviteId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.post(`/invites/${inviteId}/decline`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            await fetchMyInvites()
        } catch (error) {
            console.error('Failed to decline invite:', error)
        }
    }

    function clear() {
        myInvites.value = []
        bandInvites.value = []
        searchResults.value = []
    }

    return { 
        myInvites, 
        bandInvites,
        searchResults, 
        loading, 
        initialLoading,
        fetchMyInvites, 
        fetchBandInvites,
        searchUsers, 
        sendInvite, 
        acceptInvite, 
        declineInvite,
        clear
    }
})
